import { Avatar, AvatarFallback, Popover, PopoverContent, PopoverTrigger } from '@quadralize/ui'
import { cn, getInitials, useAuthStore } from '@quadralize/utils'
import { AltArrowDown, Buildings2, Logout, Widget } from '@solar-icons/react'
import type { ElementType } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

interface MenuItem {
  label: string
  icon: ElementType
  path: string
  iconColor: string
}

export function HeaderUserMenu() {
  const { t } = useTranslation('layout')
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const [open, setOpen] = useState(false)

  const menuItems: MenuItem[] = [
    {
      label: t('navigation.dashboard'),
      icon: Widget,
      path: '/',
      iconColor: 'text-primary',
    },
    {
      label: t('navigation.companies'),
      icon: Buildings2,
      path: '/empresas',
      iconColor: 'text-amber-600',
    },
  ]

  const profileName = user?.name ?? 'Operador'
  const profileInitials = getInitials(profileName)

  function handleNavigate(path: string) {
    setOpen(false)
    navigate(path)
  }

  function handleLogout() {
    setOpen(false)
    logout()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2.5 pl-1 sm:pl-2 group outline-none cursor-pointer"
        >
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold text-text-primary leading-none">{profileName}</p>
            <p className="text-xs text-text-tertiary mt-1">{t('shell.title')}</p>
          </div>
          <Avatar className="h-9 w-9 rounded-xl">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold rounded-xl">
              {profileInitials}
            </AvatarFallback>
          </Avatar>
          <AltArrowDown
            className={cn(
              'h-3.5 w-3.5 text-text-tertiary transition-transform hidden sm:block',
              open && 'rotate-180',
            )}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-56 p-0" sideOffset={8}>
        <div className="flex items-center gap-3 border-b border-border-subtle p-3">
          <Avatar className="h-9 w-9 rounded-lg shrink-0">
            <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-bold rounded-lg">
              {profileInitials}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-text-primary truncate">{profileName}</p>
            <p className="text-xs text-text-tertiary truncate">{t('shell.title')}</p>
          </div>
        </div>

        <div className="p-1">
          {menuItems.map((item) => {
            const Icon = item.icon
            return (
              <button
                key={item.path}
                type="button"
                onClick={() => handleNavigate(item.path)}
                className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-subtle hover:text-text-primary"
              >
                <Icon className={cn('h-4 w-4 shrink-0', item.iconColor)} weight="Bold" />
                {item.label}
              </button>
            )
          })}
        </div>

        <div className="border-t border-border-subtle p-1">
          <button
            type="button"
            onClick={handleLogout}
            className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm text-text-secondary transition-colors hover:bg-bg-subtle hover:text-error"
          >
            <Logout className="h-4 w-4 shrink-0" weight="Linear" />
            {t('userMenu.logout')}
          </button>
        </div>
      </PopoverContent>
    </Popover>
  )
}
